/**
 * "Find nearest" helpers (docs/TODO.md 3.10). Pure: runs in the geo worker pool (task `nearest`)
 * and in Jest.
 *
 * Result row (both sources):
 *   { source: 'db'|'local', type, id, name, distanceM, lonLat:[lon,lat], layerId?, layerName? }
 */
import turfDistance from '@turf/distance';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';

const NAME_KEYS = ['name', 'label', 'title', 'Name', 'id', 'ID'];

function planar(pt) {
  const k = Math.cos((pt[1] * Math.PI) / 180);
  return { k, toXY: (c) => [(c[0] - pt[0]) * k, c[1] - pt[1]] };
}

/** Nearest point on a segment a-b to p (all [lon, lat]); planar approximation around p. */
function nearestOnSegment(p, a, b) {
  const { k, toXY } = planar(p);
  const A = toXY(a), B = toXY(b);
  const dx = B[0] - A[0], dy = B[1] - A[1];
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, -(A[0] * dx + A[1] * dy) / len2)) : 0;
  return [p[0] + (A[0] + dx * t) / k, p[1] + A[1] + dy * t];
}

function nearestOnLine(p, coords) {
  let best = null;
  for (let i = 0; i < coords.length; i++) {
    const q = coords.length === 1 ? coords[0] : nearestOnSegment(p, coords[i], coords[Math.min(i + 1, coords.length - 1)]);
    const d = turfDistance(p, q, { units: 'meters' });
    if (!best || d < best.distanceM) best = { distanceM: d, lonLat: q };
  }
  return best;
}

/** Closest point of a GeoJSON geometry to `p` ([lon, lat]): { distanceM, lonLat } or null. Inside a polygon = 0. */
export function nearestOnGeometry(p, geom) {
  if (!geom) return null;
  const pick = (a, b) => (!a ? b : !b ? a : (b.distanceM < a.distanceM ? b : a));
  switch (geom.type) {
    case 'Point': return { distanceM: turfDistance(p, geom.coordinates, { units: 'meters' }), lonLat: geom.coordinates.slice(0, 2) };
    case 'MultiPoint': return geom.coordinates.reduce((acc, c) => pick(acc, nearestOnGeometry(p, { type: 'Point', coordinates: c })), null);
    case 'LineString': return nearestOnLine(p, geom.coordinates);
    case 'MultiLineString': return geom.coordinates.reduce((acc, l) => pick(acc, nearestOnLine(p, l)), null);
    case 'Polygon': {
      if (booleanPointInPolygon(p, geom)) return { distanceM: 0, lonLat: p.slice(0, 2) };
      return geom.coordinates.reduce((acc, ring) => pick(acc, nearestOnLine(p, ring)), null);
    }
    case 'MultiPolygon': return geom.coordinates.reduce((acc, poly) => pick(acc, nearestOnGeometry(p, { type: 'Polygon', coordinates: poly })), null);
    case 'GeometryCollection': return (geom.geometries || []).reduce((acc, g) => pick(acc, nearestOnGeometry(p, g)), null);
    default: return null;
  }
}

function featureName(f, index) {
  const props = f.properties || {};
  for (const key of NAME_KEYS) if (props[key] !== undefined && props[key] !== null && props[key] !== '') return String(props[key]);
  return `#${index + 1}`;
}

/**
 * Nearest features of client-side layers (unsaved layers never reach the database).
 * @param {[number, number]} point  [lon, lat]
 * @param {{id:string, name?:string, geojson:object}[]} layers
 * @param {{radiusM?:number, limit?:number}} opts
 */
export function nearestFeatures(point, layers, { radiusM = Infinity, limit = 10 } = {}) {
  const rows = [];
  for (const layer of layers || []) {
    const features = layer.geojson?.features || [];
    for (let i = 0; i < features.length; i++) {
      const f = features[i];
      const hit = nearestOnGeometry(point, f.geometry);
      if (!hit || !(hit.distanceM <= radiusM)) continue;
      rows.push({
        source: 'local', type: f.geometry.type, id: String(f.id ?? f.properties?.id ?? `${layer.id}:${i}`),
        name: featureName(f, i), distanceM: hit.distanceM, lonLat: hit.lonLat, layerId: layer.id, layerName: layer.name || layer.id,
      });
    }
  }
  return sortNearest(rows).slice(0, limit);
}

/** Server `nearby_features` rows -> result rows. */
export function dbRows(results) {
  return (results || []).map((r) => ({
    source: 'db', type: r.feature_type, id: String(r.id), name: r.name || String(r.id).slice(0, 8),
    distanceM: r.distance_m == null ? NaN : Number(r.distance_m),
    lonLat: [r.lon_out == null ? NaN : Number(r.lon_out), r.lat_out == null ? NaN : Number(r.lat_out)],
  })).filter((r) => Number.isFinite(r.distanceM) && r.lonLat.every(Number.isFinite));
}

/** Ascending distance; ties broken by source (db first), then name, so the order is stable. */
export function sortNearest(rows) {
  return [...rows].sort((a, b) => (a.distanceM - b.distanceM)
    || (a.source === b.source ? 0 : a.source === 'db' ? -1 : 1)
    || String(a.name).localeCompare(String(b.name)));
}

/**
 * Merge database and local results: drop duplicates (same source + layer + id), apply the radius,
 * sort by distance and keep `limit`. Accepts server rows (feature_type / distance_m) or result rows.
 */
export function mergeNearest(db = [], local = [], { radiusM = Infinity, limit = 10 } = {}) {
  const normalised = [
    ...db.map((r) => (r.feature_type !== undefined ? dbRows([r])[0] : r)).filter(Boolean),
    ...local,
  ];
  const seen = new Set();
  const unique = normalised.filter((r) => {
    const key = `${r.source}|${r.layerId || ''}|${r.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return Number.isFinite(r.distanceM) && r.distanceM <= radiusM;
  });
  return sortNearest(unique).slice(0, limit);
}

export function formatDistance(m) {
  if (!Number.isFinite(m)) return '';
  if (m < 1) return `${m.toFixed(2)} m`;
  if (m < 1000) return `${m.toFixed(m < 10 ? 1 : 0)} m`;
  return `${(m / 1000).toFixed(m < 10000 ? 2 : 1)} km`;
}

/** Map highlight: found features, a connector from the click, and the click point (with `label` = distance). */
export function nearestGeoJSON(point, rows) {
  const features = [];
  if (point) features.push({ type: 'Feature', properties: { role: 'origin' }, geometry: { type: 'Point', coordinates: point } });
  (rows || []).forEach((r, i) => {
    features.push({ type: 'Feature', properties: { role: 'hit', source: r.source, rank: i + 1, label: `${i + 1}. ${formatDistance(r.distanceM)}` }, geometry: { type: 'Point', coordinates: r.lonLat } });
    if (point) features.push({ type: 'Feature', properties: { role: 'link', source: r.source }, geometry: { type: 'LineString', coordinates: [point, r.lonLat] } });
  });
  return { type: 'FeatureCollection', features };
}
